<template>
  <TwoColumn ref="page404Anchor" class="max-w-screen-lg">
    <template #left>
      <h1 class="text-2xl font-bold leading-[30px]">
        {{ $t("pages.404.title") }}
      </h1>

      <p class="font-medium leading-6">
        {{ $t("pages.404.message") }}
      </p>
      <p class="font-medium leading-6">
        {{ $t("common.labels.error_contact_us_message") }}
        <a
          href="#"
          class="lowercase text-[--color-link] hover:text-[--color-neutral-a1]"
          @click.prevent="openContactUsForm"
        >
          {{ $t("common.links.contact_us") }} </a
        >.
      </p>
      <div class="mt-5 flex w-full flex-col gap-5 md:flex-row">
        <VcButton
          :to="$t('common.links.catalog_link')"
          class="h-[48px] md:!min-w-[215px]"
          color="primary"
          variant="solid"
          size="lg"
        >
          {{ $t("common.buttons.view_catalog") }}
        </VcButton>
        <VcButton to="/" class="h-[48px] md:!min-w-[118px]" color="secondary" variant="outlineTL" size="lg">
          {{ $t("common.buttons.go_home") }}
        </VcButton>
      </div>
    </template>
    <template #right>
      <VcImage
        src="/static/thorlabs/OhNo_Dog1.webp"
        :alt="$t('pages.404.img_alt')"
        class="h-[125px] w-[150px] md:h-[167px] md:w-[200px]"
        lazy
      />
    </template>
  </TwoColumn>
  <DialogForm
    ref="contactUsDialog"
    form-name="404ContactUsForm"
    :dialog-button="$t('common.buttons.send')"
    :mail-to="footerBlock?.technicalEmail"
    class="dialog--tech-support"
    should-send-gtm-generate-lead-event
  >
    <template #description>
      <div
        v-dompurify-html="
          $t('pages.feedback.family.contact_form_subtitle', [footerBlock?.technicalEmail, footerBlock?.technicalPhone])
        "
        class="mb-5 text-base font-medium leading-normal"
      ></div>
    </template>
  </DialogForm>
</template>

<script setup lang="ts">
import { useHead } from "@unhead/vue";
import { useElementVisibility } from "@vueuse/core";
import { ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useFooterBlock, usePageHead } from "@/core/composables";
import { TwoColumn } from "@/shared/layout";
import DialogForm from "@/shared/forms/components/dialog-form.vue";

const { t } = useI18n();
const { footerBlock } = useFooterBlock();
const contactUsDialog = ref<InstanceType<typeof DialogForm> | null>(null);

const page404Anchor = shallowRef<HTMLElement | null>(null);
const page404AnchorIsVisible = useElementVisibility(page404Anchor);

watch(page404AnchorIsVisible, (value) => {
  if (value) {
    usePageHead({
      title: t("pages.404.title"),
    });
    useHead({
      meta: [
        {
          name: "prerender-status-code",
          content: "404",
        },
      ],
    });
  }
});

const openContactUsForm = () => {
  contactUsDialog.value?.showDialog();
};
</script>

<style scoped lang="scss">
.dialog {
  &--tech-support {
    .dialog-content {
      a {
        @apply text-[--color-link] hover:text-[--color-neutral-a1] #{!important};
      }
    }
  }
  &-info-icons {
    &[dialog-mode="medium"] {
      @apply w-[780px] #{!important};
    }
  }
}

.vc-typography--variant--h1 {
  @apply normal-case;
}
</style>
